import * as React from 'react'
import { ErrorMessage, Field, Form, FormikProps, withFormik } from 'formik'
import * as Yup from 'yup'

import { Input } from '../elements/Input'
import { PhoneNumberInput } from '../elements/PhoneNumberInput'
import { Radio } from '../elements/Radio'
import { Textarea } from '../elements/Textarea'
import { Label } from '../elements/Label'
import { ErrorText } from '../elements/ErrorText'
import { VeteranContactFormValues } from '../../types'
import styles from './styles.module.scss'

const HOW_CAN_WE_HELP_OPTIONS = [
  'Arlington National Cemetery Services',
  'Full Veteran Services in the DMV',
]

const WHEN_ASSISTANCE_OPTIONS = ['Immediately', 'Within the next few days', "I'm planning ahead"]

const PREFERRED_CONTACT_OPTIONS = ['Phone', 'Email']

interface Props {
  onSubmit: (values: VeteranContactFormValues) => Promise<void>
  onStart?: () => void
  errorMessage?: string
}

const validationSchema = Yup.object({
  name: Yup.string().trim().required('Name is required'),
  phone: Yup.string().trim().required('Phone number is required'),
  email: Yup.string().trim().email('Invalid email address'),
  howCanWeHelp: Yup.string(),
  whenAssistance: Yup.string(),
  additionalNotes: Yup.string(),
  preferredContactMethod: Yup.string(),
})

const VeteranContactForm: React.FC<Props & FormikProps<VeteranContactFormValues>> = ({
  isSubmitting,
  errorMessage,
  onStart,
}) => {
  const hasStarted = React.useRef(false)

  const handleFirstFocus = (): void => {
    if (hasStarted.current) return
    hasStarted.current = true
    onStart?.()
  }

  return (
    <Form className={styles.form} onFocus={handleFirstFocus}>
      <div className={styles.fieldGroup}>
        <div className={styles.fieldRow}>
          <div className={styles.field}>
            <Label htmlFor="name">Name *</Label>
            <Field as={Input} name="name" placeholder="Full Name" />
            <ErrorMessage component={ErrorText} name="name" />
          </div>

          <div className={styles.field}>
            <Label htmlFor="phone">Phone *</Label>
            <Field as={PhoneNumberInput} name="phone" placeholder="+1 (___) ___-____" />
            <ErrorMessage component={ErrorText} name="phone" />
          </div>
        </div>

        <div className={styles.fieldRow}>
          <div className={styles.field}>
            <Label htmlFor="email">Email</Label>
            <Field as={Input} name="email" type="email" placeholder="Email Address" />
            <ErrorMessage component={ErrorText} name="email" />
          </div>
        </div>
      </div>

      <div className={styles.radioGroup}>
        <p className={styles.radioLabel}>How can we help?</p>
        <div className={styles.radioOptions}>
          {HOW_CAN_WE_HELP_OPTIONS.map((option) => (
            <Field key={option} as={Radio} name="howCanWeHelp" value={option} labelText={option} />
          ))}
        </div>
      </div>

      <div className={styles.radioGroup}>
        <p className={styles.radioLabel}>When do you need assistance?</p>
        <div className={styles.radioOptions}>
          {WHEN_ASSISTANCE_OPTIONS.map((option) => (
            <Field
              key={option}
              as={Radio}
              name="whenAssistance"
              value={option}
              labelText={option}
            />
          ))}
        </div>
      </div>

      <div className={styles.fieldGroup}>
        <Label htmlFor="additionalNotes">Additional Notes</Label>
        <Field
          as={Textarea}
          className={styles.textarea}
          name="additionalNotes"
          placeholder="Any additional information you'd like to share..."
        />
      </div>

      <div className={styles.radioGroup}>
        <p className={styles.radioLabel}>Preferred Contact Method</p>
        <div className={styles.radioOptions}>
          {PREFERRED_CONTACT_OPTIONS.map((option) => (
            <Field
              key={option}
              as={Radio}
              name="preferredContactMethod"
              value={option}
              labelText={option}
            />
          ))}
        </div>
      </div>

      {errorMessage && (
        <div className={styles.errorBanner}>
          <p>{errorMessage}</p>
        </div>
      )}

      <div className={styles.submitWrapper}>
        <button className={styles.submitButton} disabled={isSubmitting} type="submit">
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </button>
      </div>
    </Form>
  )
}

export default withFormik<Props, VeteranContactFormValues>({
  displayName: 'VeteranContactForm',
  handleSubmit: async (values, { props }) => {
    await props.onSubmit(values)
  },
  mapPropsToValues: () => ({
    name: '',
    phone: '',
    email: '',
    howCanWeHelp: HOW_CAN_WE_HELP_OPTIONS[0],
    whenAssistance: WHEN_ASSISTANCE_OPTIONS[0],
    additionalNotes: '',
    preferredContactMethod: PREFERRED_CONTACT_OPTIONS[0],
  }),
  validationSchema,
})(VeteranContactForm)
